import { Paginated } from "@feathersjs/feathers/lib";
import client from "feathers";
import { computed, makeObservable, observable } from "mobx";
import { DataStatus, DataType, ICompany, ICompanyWhitelist, ICurriculum, ICourse, Services, IProgramCourse } from "types";
import { Company, Course, Data, Package, Program } from ".";

export abstract class Curriculum<I extends ICurriculum = ICurriculum> extends Data<I> implements ICurriculum {
  status:      DataStatus = null;
  title:       string     = null;
  desc:        string     = null;
  start_date:  Date       = null;
  end_date:    Date       = null;
  completed?:  Date       = null;
  created_by:  number     = null;
  updated_by:  number     = null;
  progress?:   number     = null; // Integer completion percentage. Name was given to avoid ambiguity with IProgress type

  constructor(type: DataType, data: I) {
    // The super constructr takes care of references to the service, validation rules
    // and intialized the base members with mobx decorators
    super(type, data);

    // Since Mobx cannot track derived class objects automatically,
    // the decorators have to be provided manually
    makeObservable(this, {
      status:     observable,
      title:      observable,
      desc:       observable,
      start_date: observable,
      end_date:   observable,
      completed:  observable,
      progress:   observable,
      created_by: observable,
      updated_by: observable,

      isCompleted: computed,
      getProgress: computed,
      canPublish:  computed,
    });

    // Initialize data
    // While the super constructor will call this once,
    // the default values given to members will overwrite this so it has to be called again here.
    this.fromData(data);
  }

  /*******************************************************************************************************************************************
   * Database Access
   ********************************************************************************************************************************************/
  public loadCompanyOptions = async (): Promise<Company[]> => {
    const companies: Company[] = await Company.get() as Company[];
    if(this.data_type === DataType.Program) {
      return companies;
    } else if(this.data_type === DataType.Course) {
      const pcs: Paginated<IProgramCourse> = await client.service(Services.ProgramCourse).find({
        query: { course_id: this.id }
      });
      const whitelist: Paginated<ICompanyWhitelist> = await client.service(Services.CompanyWhitelist).find({
        query: { program_id: {$in: pcs.data.map(pc => pc.program_id)}}
      });
      const whitelisted_companies: number[] = whitelist.data.map(wc => Number(wc.company_id));
      return companies.filter(c => whitelisted_companies.includes(Number(c.id)));
    } else {
      return [];
    }
  }
  public loadCompanyWhitelist = async (): Promise<Company[]> => {
    if(this.data_type === DataType.Package) return [];
    try {
      const whitelist: Paginated<ICompanyWhitelist> = await client.service(Services.CompanyWhitelist).find({
        query: { [this.data_type+"_id"]: this.id }
      });
      if(!whitelist.total) return [];

      const companies: Paginated<ICompany> = await client.service(Services.Company).find({
        query: {id: {$in: whitelist.data.map(w => Number(w.company_id))}}
      });
      return companies.data.map(c => Company.create(c));
    } catch(err) {
      console.error("Failed to load company whitelist", err);
      return null;
    }
  }
  /*******************************************************************************************************************************************
   * Overwrites
   ********************************************************************************************************************************************/
  /**
    * @catalog if set, a list of all items of that content type that were published is used. 
    * 
    * Note: Here, pagination might eventually come into play!
    */
  public static get = async (id?: number, catalog?: boolean): Promise<Curriculum<ICurriculum> | Curriculum<ICurriculum>[]> => {
    return [];
  }

  public static getContent = async (content_type: DataType.Course | DataType.Program | DataType.Package, id?: number, catalog?: boolean, company_id?: number): Promise<Curriculum<ICurriculum> | Curriculum<ICurriculum>[]> => {
    switch(content_type) {
      case DataType.Package:
        return Package.get(id, catalog, company_id);
      case DataType.Program:
        return Program.get(id, catalog, undefined, company_id);
      case DataType.Course:
        return Course.get(id, catalog, undefined, company_id);
    }
  }

  public static create(data: ICurriculum): Curriculum<ICurriculum> {
    return null;
  }

  protected fromData(data: I): void {
    super.fromData(data);
    this.status     = data.status;
    this.title      = data.title;
    this.desc       = data.desc;
    this.created_by = data.created_by && Number(data.created_by);
    this.updated_by = data.updated_by && Number(data.updated_by);
    this.end_date   = data.end_date && new Date(data.end_date);
    this.start_date = data.start_date && new Date(data.start_date);
    this.completed  = data.completed && new Date(data.completed);
    this.progress   = data.completed ? 100 : data.progress;
  }

  public toData(): any {
    return {
      ...super.toData(),
      status:     this.status     || null,
      title:      this.title      || null,
      desc:       this.desc       || null,
      created_by: this.created_by || null,
      updated_by: this.updated_by || null,
      start_date: this.start_date && this.start_date.toISOString() || null,
      end_date:   this.end_date   && this.end_date.toISOString()   || null,
    };
  }

  public toPatchData(patch: Partial<I>): any {
    return {
      ...super.toPatchData(patch),
      status:     "status"     in patch ? this.status:                                        undefined,
      title:      "title"      in patch ? this.title:                                         undefined,
      desc:       "desc"       in patch ? this.desc:                                          undefined,
      created_by: "created_by" in patch ? this.created_by:                                    undefined,
      updated_by: "updated_by" in patch ? this.updated_by:                                    undefined,
      start_date: "start_date" in patch ? patch.start_date && patch.start_date.toISOString(): undefined,
      end_date:   "end_date"   in patch ? patch.end_date && patch.end_date.toISOString():     undefined,
    };
  }

  /*******************************************************************************************************************************************
   * Getter
   ********************************************************************************************************************************************/

  /**
    * Placeholder function. Has no use for now
    */
  public get canEdit(): boolean {
    return true;
  }

  /**
   * @return true if content was already completed by the current user. 
   * 
   * Note: The backend will prevent the user from loading stepts related to
   * content once it is completed. 
   * Note: The backend will not prevent the user from loading steps any longer
   * even once the course is complete as per client's request.
   */
  public get isCompleted(): boolean {
    return Boolean(this.completed);
  }

  /**
   * @return progress percentage as integer
   */
  public abstract get getProgress(): number;

  /**
   * @return true if the course can be published, i.e. all quizzes are valid.
   */
  public abstract get canPublish(): boolean;

  /*******************************************************************************************************************************************
   * Setter
   ********************************************************************************************************************************************/

  /*******************************************************************************************************************************************
   * Actions
   ********************************************************************************************************************************************/
  public addCompanyToWhitelist = async (company_id: number): Promise<boolean> => {
    if(this.data_type === DataType.Package) return;
    try {
      await client.service(Services.CompanyWhitelist).create({
        company_id,
        [this.data_type+"_id"]: this.id
      });
      return true;
    } catch(err) {
      console.error("Failed to update company whitelist", err);
      return false;
    }
  }
  public removeCompanyFromWhitelist = async (company_id: number): Promise<boolean> => {
    if(this.data_type === DataType.Package) return;
    try {
      const whitelist: Paginated<ICompanyWhitelist> = await client.service(Services.CompanyWhitelist).find({
        query: {
          company_id,
          [this.data_type+"_id"]: this.id
        }
      });
      if(!whitelist.total) throw new Error("Failed to find whitelist entry");
      await client.service(Services.CompanyWhitelist).remove(whitelist.data[0].id);
      return true; 
    } catch(err) {
      console.error("Failed to update company whitelist", err);
      return false;
    }
  }
}
